import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postForm } from '@/shared/api/api-client';

/**
 * Bulk patient import via CSV upload.
 *
 * Wire (Flutter `common_data_source_impl.dart:774`):
 *   POST /api/Patients/TemplateCSVFileUpload
 *        ?OrganizationId=<orgId>
 *        &PracticeId=<practiceId>
 *        &createdBy=<userId>   // GUID, NOT an email/name — see security note
 *   Content-Type: multipart/form-data
 *   Form field: `MultipartDataCSVFile` (MIME: text/csv)
 *
 *   200 OK → UploadBulkPatientResponse
 *     {
 *       patientBulkImportID: string,
 *       filepath: string | null,   // URL to error-CSV when there are errors
 *       totalcount: string,        // failed-row count, as STRING ("0" = all good)
 *       uploadProgress: number | null,
 *     }
 *
 * **Important wire quirk — `totalcount` is a STRING.** See the
 * comparison in `onSuccess` for the doc-anchored explanation.
 *
 * **SECURITY notes** (Samer PR #99 review 2026-05-14):
 *
 *   - `createdBy` is the logged-in user's GUID (read from
 *     `auth-store.user.userId`), NOT a name or email. The .NET backend
 *     historically accepts it as a query param for Flutter parity, but
 *     the authoritative trust anchor is the Bearer JWT (`sub` claim) +
 *     the `UserId` header the api-client injects on every request. The
 *     backend MUST cross-check `createdBy` against the JWT — if it
 *     accepts an arbitrary `createdBy` from the URL, that's a server
 *     bug to file. Tracked as a backend audit gap: **PROD-714 follow-up
 *     — confirm /TemplateCSVFileUpload binds createdBy from JWT, not the
 *     query string**.
 *
 *   - `Manage_Patients_Demographics.edit` is checked client-side (in
 *     `patients-list-page.tsx`) but client gates are UX, not security.
 *     The backend MUST enforce the same permission on this endpoint —
 *     otherwise a direct POST bypasses the gate entirely. Same
 *     backend-audit follow-up as above.
 *
 *   - `filepath` in the response is a backend-generated URL to the
 *     error-CSV blob. Must be **per-tenant scoped** (signed URL with
 *     orgId-bound key) — if it's a guessable static path, failed-import
 *     data (which contains patient demographics) leaks cross-tenant.
 *     Tracked as: **PROD-714 follow-up — confirm error-CSV URL is
 *     org-scoped + signed**.
 *
 * Cache: invalidates `['patients-list']` on success so the table picks
 * up new rows.
 */

export interface UploadBulkPatientsArgs {
  file: File;
  organizationId: string;
  practiceId: string;
  createdBy: string;
}

export interface UploadBulkPatientResponse {
  patientBulkImportID?: string | null;
  /** URL to a downloadable error CSV (only set when totalcount > 0). */
  filepath?: string | null;
  /** Failed-row count as a STRING. "0" means success. */
  totalcount?: string | null;
  uploadProgress?: number | null;
}

export function useUploadBulkPatients() {
  const qc = useQueryClient();
  return useMutation<UploadBulkPatientResponse, Error, UploadBulkPatientsArgs>({
    mutationFn: async ({ file, organizationId, practiceId, createdBy }) => {
      const params = new URLSearchParams({
        OrganizationId: organizationId,
        PracticeId: practiceId,
        createdBy,
      });
      const body = new FormData();
      // Flutter uses MIME `text/csv`; setting it on the Blob ensures
      // the backend's content-type sniffer accepts it even for files
      // that the OS labelled `application/vnd.ms-excel` (older Excel
      // CSV exports).
      const typedFile = new File([file], file.name, { type: 'text/csv' });
      body.append('MultipartDataCSVFile', typedFile);
      return postForm<UploadBulkPatientResponse>(
        `/api/Patients/TemplateCSVFileUpload?${params.toString()}`,
        body,
      );
    },
    onSuccess: (data) => {
      // Wire contract: `totalcount` is a STRING, not a number. The
      // server ships "0" / "5" / etc., and Flutter compares with
      // `== "0"` — we match that. DO NOT "fix" this to a numeric
      // compare or `Number(totalcount) === 0`: the response body
      // sometimes carries `null` / undefined for the field, and a
      // coerced `Number(null) === 0` would falsely flag broken
      // imports as clean. String equality is the safe check.
      // Samer PR #99 review 2026-05-14.
      if (data.totalcount === '0') {
        // Only refresh the list when the import was clean. With errors,
        // the user is still in the dialog reviewing — refetching now
        // could surprise them with rows they haven't acknowledged.
        void qc.invalidateQueries({ queryKey: ['patients-list'] });
      }
    },
  });
}
